import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { AI_RECOMMENDATION_IMPRESSION_GA4_EVENT } from '../../services/event-tracking/index'
import { selectAuth } from '../../services/auth/selectors'
import { usePostHogEvent, AiRecommendationPayload } from '../PostHog/index'
import { MediaTile, RelatedDocument } from './MediaTile'
import { buildAiRecommendationEventData, trackAiRecommendationClick } from './utils/aiRecommendationTracking'

const toRecommendationPayload = (
  document: RelatedDocument,
  uiElement: AiRecommendationPayload['ui_element'],
): AiRecommendationPayload => ({
  ui_element: uiElement,
  content_id: String(document.contentId),
  content_type: document.contentType,
  content_name: document.title,
  parent_series_id: document.parentSeriesId != null ? String(document.parentSeriesId) : null,
  parent_series_name: document.parentSeriesName ?? null,
  media_id: document.mediaId != null ? String(document.mediaId) : null,
  media_language: document.mediaLanguage ?? null,
  is_featured_media: document.isFeaturedMedia ?? null,
})

export type MediaTileLayoutDirection = 'row' | 'column'

interface MediaTileCollectionProps {
  documents: RelatedDocument[]
  layout?: MediaTileLayoutDirection
  maxTiles?: number
}

interface MediaTileItemProps {
  document: RelatedDocument
}

/**
 * MediaTileItem - Individual tile with impression tracking
 * Tracks when the tile becomes visible and dispatches ai_recommendation_impression event
 */
const MediaTileItem: React.FC<MediaTileItemProps> = ({ document }) => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const itemRef = useRef<HTMLDivElement>(null)
  const [hasTracked, setHasTracked] = useState(false)
  const trackRecommendationViewed = usePostHogEvent('ai_recommendation_viewed')
  const trackRecommendationClicked = usePostHogEvent('ai_recommendation_clicked')

  const handleClick = () => {
    trackAiRecommendationClick(document, dispatch, auth)
    trackRecommendationClicked(toRecommendationPayload(document, 'Cover Tile'))
  }

  useEffect(() => {
    const element = itemRef.current
    if (!element || hasTracked || !window.IntersectionObserver) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTracked) {
          const eventData = buildAiRecommendationEventData(document)
          dispatch(setAiGa4Event(AI_RECOMMENDATION_IMPRESSION_GA4_EVENT.mergeDeep(eventData)))
          trackRecommendationViewed(toRecommendationPayload(document, 'Cover Tile'))

          setHasTracked(true)
          observer.unobserve(element)
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1, // Trigger when 10% of the element is visible
      },
    )

    observer.observe(element)

    return () => {
      if (element) {
        observer.unobserve(element)
      }
    }
  }, [
    document.contentId,
    document.contentType,
    document.title,
    document.path,
    document.thumb,
    dispatch,
    hasTracked,
  ])

  return (
    <div ref={itemRef} className="media-tile-collection__item">
      <MediaTile document={document} onClick={handleClick} />
    </div>
  )
}

/**
 * MediaTileCollection - Displays a collection of video tiles
 *
 * Features:
 * - Supports both row and column layouts
 * - Tiles scale on hover without affecting overall layout
 * - Hovered tiles overlap neighboring tiles
 * - Responsive spacing and layout
 * - Tracks AI recommendation impressions when tiles become visible
 */
export const MediaTileCollection: React.FC<MediaTileCollectionProps> = ({
  documents,
  layout = 'row',
  maxTiles,
}) => {
  // Limit the number of tiles if maxTiles is specified
  const displayedDocuments = maxTiles ? documents.slice(0, maxTiles) : documents

  if (displayedDocuments.length === 0) {
    return null
  }

  const layoutClass = layout === 'column' ? 'media-tile-collection--column' : 'media-tile-collection--row'

  return (
    <div className={`media-tile-collection ${layoutClass}`}>
      {displayedDocuments.map((document) => (
        <MediaTileItem key={document.contentId} document={document} />
      ))}
    </div>
  )
}
